import { useEffect, useRef, memo } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Conversation, ConversationViewMode } from "@/types/conversation/conversation";
import { ConversationListItem } from "./ConversationListItem";
import { ConversationRowsView } from "./ConversationRowsView";
import { ConversationTableView } from "./ConversationTableView";

interface ConversationListProps {
  conversations: Conversation[];
  isLoading: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  viewMode: ConversationViewMode;
  onViewModeChange: (mode: ConversationViewMode) => void;
  onViewDetails: (conversation: Conversation) => void;
  onMarkAllAsReviewed?: () => void;
  onLoadMore?: () => void;
  /** Replaces the default "No conversations yet" message, e.g. for an inbox queue. */
  emptyMessage?: EmptyMessage;
}

interface EmptyMessage {
  title: string;
  description: string;
}

export const ConversationList = memo(
  ({
    conversations,
    isLoading,
    isFetchingNextPage = false,
    hasNextPage = false,
    viewMode,
    onViewDetails,
    onLoadMore,
    emptyMessage,
  }: ConversationListProps) => {
    const loadMoreRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;

      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            onLoadMore();
          }
        },
        { threshold: 0.1 },
      );

      if (loadMoreRef.current) {
        observer.observe(loadMoreRef.current);
      }

      return () => observer.disconnect();
    }, [onLoadMore, hasNextPage, isFetchingNextPage]);

    return (
      <div className="space-y-6">
        {isLoading ? (
          <LoadingSkeleton viewMode={viewMode} />
        ) : conversations.length === 0 ? (
          <EmptyState message={emptyMessage} />
        ) : (
          <>
            {viewMode === "cards" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
                {conversations.map((conversation) => (
                  <ConversationListItem
                    key={conversation.id}
                    conversation={conversation}
                    onViewDetails={onViewDetails}
                    isViewed={conversation.viewed || false}
                  />
                ))}
              </div>
            )}

            {viewMode === "rows" && (
              <ConversationRowsView
                conversations={conversations}
                onViewDetails={onViewDetails}
              />
            )}

            {viewMode === "table" && (
              <ConversationTableView
                conversations={conversations}
                onViewDetails={onViewDetails}
              />
            )}

            {hasNextPage && (
              <div ref={loadMoreRef} className="flex justify-center py-8">
                {isFetchingNextPage ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading more conversations...</span>
                  </div>
                ) : (
                  <Button onClick={onLoadMore} variant="outline" className="px-6 py-2">
                    Load More Conversations
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  },
);

ConversationList.displayName = "ConversationList";

const LoadingSkeleton = ({ viewMode }: { viewMode: ConversationViewMode }) => {
  if (viewMode === "table") {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 last:border-b-0 animate-pulse"
          >
            <div className="w-8 h-8 bg-gray-200 rounded-full" />
            <div className="flex-1 h-3 bg-gray-200 rounded" />
            <div className="w-16 h-3 bg-gray-200 rounded" />
            <div className="w-16 h-3 bg-gray-200 rounded" />
            <div className="w-12 h-3 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (viewMode === "rows") {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="bg-white rounded-2xl border-2 border-gray-100 p-5 animate-pulse flex items-center gap-4"
          >
            <div className="w-12 h-12 bg-gray-200 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-gray-200 rounded w-32" />
              <div className="h-3 bg-gray-200 rounded w-24" />
            </div>
            <div className="w-24 h-3 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div key={i} className="bg-white rounded-2xl shadow-lg p-6 animate-pulse">
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 bg-gray-200 rounded-full mb-4" />
            <div className="h-5 bg-gray-200 rounded w-32 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-24 mb-4" />
            <div className="flex gap-2 mb-4">
              <div className="h-5 bg-gray-200 rounded-full w-16" />
              <div className="h-5 bg-gray-200 rounded-full w-16" />
            </div>
            <div className="h-10 bg-gray-200 rounded-full w-full" />
          </div>
        </div>
      ))}
    </div>
  );
};

const DEFAULT_EMPTY_MESSAGE: EmptyMessage = {
  title: "No conversations yet",
  description: "When people start chatting with your genie, conversations will appear here.",
};

const EmptyState = ({ message = DEFAULT_EMPTY_MESSAGE }: { message?: EmptyMessage }) => (
  <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 shadow-lg">
    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{message.title}</h3>
    <p className="text-gray-500">{message.description}</p>
  </div>
);
